import alert_mixin from '@/utils/mixins/alert_mixin';
import { create_form_status, edit_form_status, fetch_form_statuses } from '@/services/form_metadata_service';

export default {
  name: 'ManageStatus',
  mixins: [alert_mixin],
  data: () => ({
    statuses_loading: false,
    status_creating: false,
    status_updating: false,
    selected_status_id: '',
    new_status_name: '',
    new_status_color: '#000000',
    edit_status_id: '',
    edit_status_name: '',
    edit_status_color: '#000000',
    statuses: [],
  }),
  created() {
    this.load_statuses();
  },
  computed: {
    workflow_query() {
      return {
        workflowId: String(this.$route?.query?.workflowId || '').trim(),
        workflowName: String(this.$route?.query?.workflowName || '').trim(),
        entityTypeId: String(this.$route?.query?.entityTypeId || '').trim(),
        projectId: String(this.$route?.query?.projectId || '').trim(),
      };
    },
  },
  methods: {
    normalize_status: (status = {}, index = 0) => ({
      id: String(status?.id ?? index),
      rawId: status?.id ?? index,
      name: String(status?.status || status?.name || `Status ${index + 1}`).trim(),
      description: `Available workflow state${status?.id ? ` - ID ${status.id}` : ''}`,
      badge: 'Existing Status',
      badgeClass: 'text-dark bg-light',
      note: status?.color_code || '#6c757d',
      noteClass: '',
      colorCode: status?.color_code || '#6c757d',
    }),

    initialize_status_selection() {
      if (!this.statuses.length) {
        this.selected_status_id = '';
        return;
      }

      const has_selected_status = this.statuses.some((status) => status.id === this.selected_status_id);

      if (!has_selected_status) {
        this.selected_status_id = this.statuses[0].id;
      }
    },

    reset_create_status_form() {
      this.new_status_name = '';
      this.new_status_color = '#000000';
    },

    open_edit_status_modal(status = {}) {
      this.edit_status_id = String(status?.rawId ?? status?.id ?? '');
      this.edit_status_name = String(status?.name || '').trim();
      this.edit_status_color = String(status?.colorCode || '#000000').trim() || '#000000';
    },

    build_create_status_payload() {
      const status_name = String(this.new_status_name || '').trim();
      const color_code = String(this.new_status_color || '').trim();

      if (!status_name) {
        this.show_error('Please enter a status name.');
        return;
      }

      if (!/^#[0-9A-Fa-f]{6}$/.test(color_code)) {
        this.show_error('Please choose a valid hex color.');
        return;
      }

      const payload = { status: status_name, color_code };
      return payload;
    },

    build_edit_status_payload() {
      const status_id = Number(this.edit_status_id);
      const status_name = String(this.edit_status_name || '').trim();
      const color_code = String(this.edit_status_color || '').trim();

      if (!Number.isInteger(status_id) || status_id <= 0) {
        this.show_error('Please select a valid status to edit.');
        return;
      }

      if (!status_name) {
        this.show_error('Please enter a status name.');
        return;
      }

      if (!/^#[0-9A-Fa-f]{6}$/.test(color_code)) {
        this.show_error('Please choose a valid hex color.');
        return;
      }

      const payload = { status_id, status: status_name, color_code };
      return payload;
    },

    async load_statuses() {
      this.statuses_loading = true;

      try {
        const statuses = await fetch_form_statuses();
        this.statuses = Array.isArray(statuses)
          ? statuses.map((status, index) => this.normalize_status(status, index))
          : [];
        this.initialize_status_selection();
      } catch {
        this.statuses = [];
        this.initialize_status_selection();
      } finally {
        this.statuses_loading = false;
      }
    },

    async save_status() {
      const payload = this.build_create_status_payload();
      if (!payload) {
        return;
      }

      this.status_creating = true;
      const loader = await this.show_loader('Creating status...');

      try {
        const response = await create_form_status(payload);
        await this.load_statuses();
        this.reset_create_status_form();
        this.close_modal('create_status_modal');

        const created_status_id = response?.data?.id;
        if (created_status_id !== undefined && created_status_id !== null) {
          this.selected_status_id = String(created_status_id);
        }

        this.show_success(response?.message || 'Form status created successfully.');
      } catch (error) {
        this.show_error(
          error?.response?.data?.message
          || error?.message
          || 'Failed to create form status.'
        );
      } finally {
        await this.close_loader(loader);
        this.status_creating = false;
      }
    },

    async update_status() {
      const payload = this.build_edit_status_payload();
      if (!payload) {
        return;
      }

      this.status_updating = true;
      const loader = await this.show_loader('Updating status...');

      try {
        const response = await edit_form_status(payload);
        await this.load_statuses();
        this.selected_status_id = String(payload.status_id);
        this.close_modal('edit_status_modal');
        this.show_success(response?.message || 'Form Status updated successfully');
      } catch (error) {
        this.show_error(
          error?.response?.data?.message
          || error?.message
          || 'Failed to update form status.'
        );
      } finally {
        await this.close_loader(loader);
        this.status_updating = false;
      }
    },

    go_to_configure_workflow() {
      this.$router.push({
        name: 'ConfigureWorkflow',
        query: {
          workflowId: this.workflow_query.workflowId,
          workflowName: this.workflow_query.workflowName,
          entityTypeId: this.workflow_query.entityTypeId,
          projectId: this.workflow_query.projectId,
        },
      });
    },
  },
};
